import { useState, useCallback, useEffect, useMemo } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { ProjectImages, ProjectImageUploadDialog } from './types';
import { ProjectImagesService } from './projectImagesService';
import { toast } from 'sonner';

export const useProjectImages = (projectInstanceId: string, userId: string) => {
  const services = useServices();
  const [projectImages, setProjectImages] = useState<ProjectImages>({
    portrait: null,
    landscape: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The images could not be read: the page shows Retry and offers no upload over what it cannot see
  const [readFailed, setReadFailed] = useState(false);
  const [imageUploadDialog, setImageUploadDialog] = useState<ProjectImageUploadDialog>({ open: false });

  // Initialize service - use useMemo to prevent recreation on every render
  const projectImagesService = useMemo(() => new ProjectImagesService(services), [services]);

  // Load images into state; callers set the loading state before awaiting it
  const loadProjectImages = useCallback(() => {
    return projectImagesService.fetchProjectImages(projectInstanceId)
      .then((images) => {
        setProjectImages(images);
        setReadFailed(false);
      })
      .catch((err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch project images';
        setError(errorMessage);
        setReadFailed(true);
        console.error('Error fetching project images:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectInstanceId, projectImagesService]);

  // Refetch project images on demand
  const fetchProjectImages = useCallback(async () => {
    if (!projectInstanceId) return;

    setLoading(true);
    setError(null);

    await loadProjectImages();
  }, [projectInstanceId, loadProjectImages]);

  // Show the loading state as soon as the project (or service) changes, including on mount
  const [loadStartedFor, setLoadStartedFor] = useState<{ id: string; service: ProjectImagesService } | null>(null);
  if (loadStartedFor?.id !== projectInstanceId || loadStartedFor.service !== projectImagesService) {
    setLoadStartedFor({ id: projectInstanceId, service: projectImagesService });
    if (projectInstanceId) {
      setLoading(true);
      setError(null);
    }
  }

  // Load images on mount or when projectInstanceId changes
  useEffect(() => {
    if (!projectInstanceId) return;
    loadProjectImages();
  }, [projectInstanceId, loadProjectImages]);

  // Handle image upload dialog
  const handleImageUpload = useCallback((imageCategory: 'ProjectPortrait' | 'ProjectLandscape', imageId?: string) => {
    if (readFailed) {
      toast.error("We couldn't load the project images. Try again before uploading.");
      return;
    }
    setImageUploadDialog({
      open: true,
      imageCategory,
      title: `Upload ${imageCategory === 'ProjectPortrait' ? 'Portrait' : 'Landscape'} Image`,
      imageId
    });
  }, [readFailed]);

  // Handle image selection (upload/update)
  const handleImageSelect = useCallback(async (imageUrl: string) => {
    if (!projectInstanceId || !userId) return;
    
    const imageCategory = imageUploadDialog.imageCategory;
    const imageId = imageUploadDialog.imageId;
    if (!imageCategory) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (imageUrl) {
        if (imageId) {
          // Update existing image
          await projectImagesService.updateProjectImage(imageId, imageUrl);
          toast.success('Image updated successfully');
        } else {
          // Create new image
          await projectImagesService.createProjectImage(projectInstanceId, userId, imageCategory, imageUrl);
          toast.success('Image uploaded successfully');
        }
        
        // Refresh images to get latest state
        await fetchProjectImages();
      } else {
        // Image was removed
        if (imageId) {
          await projectImagesService.deleteProjectImage(imageId);
          toast.success('Image removed successfully');
          await fetchProjectImages();
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process image';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error processing image:', err);
    } finally {
      setLoading(false);
    }
  }, [projectInstanceId, userId, imageUploadDialog, projectImagesService, fetchProjectImages]);

  // Handle crop applied (new image upload or re-crop)
  const handleCropApplied = useCallback(async (data: { 
    imageOriginalUrl: string; 
    imageCroppedUrl: string; 
    crop: { x: number; y: number; width: number; height: number } 
  }) => {
    if (!projectInstanceId || !userId) return;
    
    const imageCategory = imageUploadDialog.imageCategory;
    const imageId = imageUploadDialog.imageId;
    if (!imageCategory) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (imageId) {
        // Update existing image with new URLs and crop metadata
        await projectImagesService.updateProjectImage(imageId, data.imageOriginalUrl, data.imageCroppedUrl, { crop: data.crop });
        toast.success('Image updated and cropped successfully');
      } else {
        // Create new image with URLs and crop metadata
        await projectImagesService.createProjectImage(projectInstanceId, userId, imageCategory, data.imageOriginalUrl, data.imageCroppedUrl, { crop: data.crop });
        toast.success('Image uploaded and cropped successfully');
      }
      
      // Refresh images to get latest state
      await fetchProjectImages();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process image crop';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error processing image crop:', err);
    } finally {
      setLoading(false);
    }
  }, [projectInstanceId, userId, imageUploadDialog, projectImagesService, fetchProjectImages]);

  // Handle image deletion: the image leaves the page once the server has removed it
  const handleImageDelete = useCallback(async (imageId: string, imageCategory: 'ProjectPortrait' | 'ProjectLandscape') => {
    if (!projectInstanceId) return;

    setLoading(true);
    setError(null);

    try {
      await projectImagesService.deleteProjectImage(imageId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete image';
      setError(errorMessage);
      toast.error("We couldn't delete the image. It's still on the project.");
      console.error('Error deleting image:', err);
      setLoading(false);
      return;
    }

    if (imageCategory === 'ProjectPortrait') {
      setProjectImages(prev => ({ ...prev, portrait: null }));
    } else {
      setProjectImages(prev => ({
        ...prev,
        landscape: prev.landscape.filter(img => img.id !== imageId).map((img, index) => ({ ...img, orderNumber: index + 1 })),
      }));
      try {
        await projectImagesService.reorderAfterDeletion(projectInstanceId);
      } catch (err) {
        console.error('Error updating order numbers after deletion:', err);
        toast.error("The image was deleted, but we couldn't save the new order of the others.");
        setLoading(false);
        await fetchProjectImages();
        return;
      }
    }
    toast.success('Image deleted successfully');
    setLoading(false);
  }, [projectInstanceId, projectImagesService, fetchProjectImages]);

  // Handle landscape image reordering: shown at once, put back if the server did not take it
  const handleLandscapeImageReorder = useCallback(async (event: { active: { id: string }, over: { id: string } | null }) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const previous = projectImages.landscape;
    const oldIndex = previous.findIndex(img => img.id === active.id);
    const newIndex = previous.findIndex(img => img.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const landscape = [...previous];
    const [movedImage] = landscape.splice(oldIndex, 1);
    landscape.splice(newIndex, 0, movedImage);
    const reordered = landscape.map((img, index) => ({ ...img, orderNumber: index + 1 }));
    setProjectImages(prev => ({ ...prev, landscape: reordered }));

    try {
      await projectImagesService.reorderLandscapeImages(projectInstanceId, reordered);
      toast.success('Image order updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save image order';
      setError(errorMessage);
      console.error('Error updating image order numbers:', err);
      toast.error("We couldn't save the new image order.");
      setProjectImages(prev => ({ ...prev, landscape: previous }));
      // Some images may have taken their new number before the failure
      await fetchProjectImages();
    }
  }, [projectInstanceId, projectImagesService, projectImages.landscape, fetchProjectImages]);

  // Close upload dialog
  const closeImageUploadDialog = useCallback(() => {
    setImageUploadDialog({ open: false });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    projectImages,
    loading,
    error,
    readFailed,
    imageUploadDialog,
    
    // Actions
    handleImageUpload,
    handleImageSelect,
    handleCropApplied,
    handleImageDelete,
    handleLandscapeImageReorder,
    closeImageUploadDialog,
    clearError,
    fetchProjectImages
  };
};
